import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, MoreVertical, Trash2, ChevronLeft, ChevronRight, Zap, Play, Pause, Settings, History } from 'lucide-react'
import { toast } from 'sonner'
import { automationApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatDate } from '@/lib/utils'
import type { AutomationRule, TriggerType } from '@/types'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_TRIGGERS = '__all__'

const TRIGGER_LABELS: Record<TriggerType, string> = {
  LEAD_CREATED: 'Lead Created',
  LEAD_STATUS_CHANGED: 'Lead Status Changed',
  LEAD_ASSIGNED: 'Lead Assigned',
  LEAD_CONVERTED: 'Lead Converted',
  DEAL_CREATED: 'Deal Created',
  DEAL_STAGE_CHANGED: 'Deal Stage Changed',
  DEAL_WON: 'Deal Won',
  DEAL_LOST: 'Deal Lost',
  DEAL_ASSIGNED: 'Deal Assigned',
  ACTIVITY_CREATED: 'Activity Created',
  TASK_COMPLETED: 'Task Completed',
  TASK_OVERDUE: 'Task Overdue',
  REMINDER_DUE: 'Reminder Due',
}

const TRIGGER_COLORS: Record<string, string> = {
  LEAD: 'bg-blue-100 text-blue-800',
  DEAL: 'bg-green-100 text-green-800',
  ACTIVITY: 'bg-purple-100 text-purple-800',
  TASK: 'bg-orange-100 text-orange-800',
  REMINDER: 'bg-yellow-100 text-yellow-800',
}

function getTriggerCategory(trigger: TriggerType): string {
  if (trigger.startsWith('LEAD_')) return 'LEAD'
  if (trigger.startsWith('DEAL_')) return 'DEAL'
  if (trigger.startsWith('ACTIVITY_')) return 'ACTIVITY'
  if (trigger.startsWith('TASK_')) return 'TASK'
  if (trigger.startsWith('REMINDER_')) return 'REMINDER'
  return 'OTHER'
}

export default function AutomationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [ruleToDelete, setRuleToDelete] = useState<AutomationRule | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [triggerFilter, setTriggerFilter] = useState<string>(ALL_TRIGGERS)

  const { data: automationsResponse, isLoading } = useQuery({
    queryKey: ['automations', currentPage, pageSize, triggerFilter],
    queryFn: () => automationApi.getAutomations({
      page: currentPage,
      page_size: pageSize,
      trigger_type: triggerFilter !== ALL_TRIGGERS ? triggerFilter as TriggerType : undefined,
    }),
  })

  const deleteMutation = useMutation({
    mutationFn: (ruleId: string) => automationApi.deleteAutomation(ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] })
      toast.success(t('crm.automations.deleteSuccess', 'Automation deleted successfully'))
      setDeleteDialogOpen(false)
      setRuleToDelete(null)
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error))
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ ruleId, isActive }: { ruleId: string; isActive: boolean }) =>
      automationApi.toggleAutomation(ruleId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] })
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error))
    },
  })

  const rules = automationsResponse?.rules || []
  const totalPages = automationsResponse?.total_pages || 1
  const total = automationsResponse?.total || 0

  const handleDelete = (rule: AutomationRule) => {
    setRuleToDelete(rule)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (ruleToDelete) {
      deleteMutation.mutate(ruleToDelete.rule_id)
    }
  }

  const handleToggle = (rule: AutomationRule) => {
    toggleMutation.mutate({ ruleId: rule.rule_id, isActive: !rule.is_active })
  }

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setCurrentPage(1)
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Zap className="h-8 w-8 text-yellow-500" />
            {t('crm.automations.title', 'Automations')}
          </h1>
          <p className="text-muted-foreground">
            {t('crm.automations.description', 'Automate your CRM workflows with triggers and actions')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/crm/automations/logs')}>
            <History className="mr-2 h-4 w-4" />
            {t('crm.automations.viewLogs', 'View Logs')}
          </Button>
          <Button onClick={() => navigate('/crm/automations/new')}>
            <Plus className="mr-2 h-4 w-4" />
            {t('crm.automations.create', 'Create Automation')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="w-64">
              <Select value={triggerFilter} onValueChange={setTriggerFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t('crm.automations.filterByTrigger', 'Filter by trigger')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_TRIGGERS}>{t('common.all', 'All Triggers')}</SelectItem>
                  {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-muted-foreground">
              {t('crm.automations.totalRules', '{{count}} automation rules', { count: total })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Automations Table */}
      <Card>
        <CardContent className="p-0">
          <div className="h-[calc(100vh-350px)] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-12">{t('common.status', 'Status')}</TableHead>
                  <TableHead>{t('crm.automations.ruleName', 'Rule Name')}</TableHead>
                  <TableHead>{t('crm.automations.trigger', 'Trigger')}</TableHead>
                  <TableHead className="text-center">{t('crm.automations.conditions', 'Conditions')}</TableHead>
                  <TableHead className="text-center">{t('crm.automations.actions', 'Actions')}</TableHead>
                  <TableHead>{t('common.createdAt', 'Created')}</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      {t('common.loading', 'Loading...')}
                    </TableCell>
                  </TableRow>
                ) : rules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2">
                        <Zap className="h-12 w-12 text-muted-foreground/50" />
                        <p className="text-muted-foreground">
                          {t('crm.automations.noRules', 'No automation rules found')}
                        </p>
                        <Button variant="outline" size="sm" onClick={() => navigate('/crm/automations/new')}>
                          <Plus className="mr-2 h-4 w-4" />
                          {t('crm.automations.createFirst', 'Create your first automation')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  rules.map((rule: AutomationRule) => (
                    <TableRow key={rule.rule_id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell>
                        <Switch
                          checked={rule.is_active}
                          onCheckedChange={() => handleToggle(rule)}
                          disabled={toggleMutation.isPending}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{rule.rule_name}</div>
                          {rule.description && (
                            <div className="text-sm text-muted-foreground truncate max-w-xs">
                              {rule.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={TRIGGER_COLORS[getTriggerCategory(rule.trigger_type)]}>
                          {TRIGGER_LABELS[rule.trigger_type] || rule.trigger_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{rule.conditions?.length || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{rule.actions?.length || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(rule.created_at)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/crm/automations/${rule.rule_id}`)}>
                              <Settings className="mr-2 h-4 w-4" />
                              {t('common.edit', 'Edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggle(rule)}>
                              {rule.is_active ? (
                                <>
                                  <Pause className="mr-2 h-4 w-4" />
                                  {t('crm.automations.disable', 'Disable')}
                                </>
                              ) : (
                                <>
                                  <Play className="mr-2 h-4 w-4" />
                                  {t('crm.automations.enable', 'Enable')}
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDelete(rule)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('common.delete', 'Delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t('common.rowsPerPage', 'Rows per page')}:</span>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>{size}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm">
            {t('common.pageOf', 'Page {{current}} of {{total}}', { current: currentPage, total: totalPages })}
          </span>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('crm.automations.deleteTitle', 'Delete Automation')}</DialogTitle>
            <DialogDescription>
              {t('crm.automations.deleteConfirm', 'Are you sure you want to delete "{{name}}"? This action cannot be undone.', { name: ruleToDelete?.rule_name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

